import type { IceServer } from '../api/calls';

type CandidateCb = (candidate: RTCIceCandidateInit) => void;
type StreamCb = (stream: MediaStream) => void;
type StateCb = (state: RTCPeerConnectionState) => void;

/**
 * High-quality mic capture constraints. echoCancellation + noiseSuppression +
 * autoGainControl clean up the signal; mono (channelCount 1) keeps voice tight
 * and halves the data vs. stereo for the same clarity; 48 kHz feeds Opus its
 * full wideband range. Falls back to a bare `{ audio: true }` if a browser
 * rejects the constraint set, so capture never breaks.
 */
const AUDIO_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    sampleRate: 48000,
  },
};

/** Target Opus bitrate (bits/s). ~48 kbps is transparent for speech. */
const OPUS_MAX_BITRATE = 48000;

/**
 * Rewrite the local SDP so Opus runs in its clearest mode:
 *   useinbandfec=1   — forward error correction; reconstructs dropped packets
 *                      (the single biggest clarity win on lossy networks)
 *   usedtx=1         — discontinuous transmission; don't send silence
 *   maxaveragebitrate / maxplaybackrate — full wideband audio
 *   stereo=0; sprop-stereo=0 — mono, matches the capture track
 * Also hoists Opus to the front of the m=audio codec list so it's preferred.
 */
function tuneOpusSdp(sdp: string): string {
  const lines = sdp.split(/\r\n|\n/);
  // Find Opus payload type from its rtpmap line.
  const rtpmap = lines.find((l) => /^a=rtpmap:\d+\s+opus\/48000/i.test(l));
  if (!rtpmap) return sdp;
  const pt = rtpmap.match(/^a=rtpmap:(\d+)/)?.[1];
  if (!pt) return sdp;

  const params = [
    'minptime=10',
    'useinbandfec=1',
    'usedtx=1',
    'stereo=0',
    'sprop-stereo=0',
    `maxplaybackrate=48000`,
    `maxaveragebitrate=${OPUS_MAX_BITRATE}`,
  ].join(';');

  const out = lines.map((line) => {
    // Augment (or replace) the Opus fmtp line.
    if (new RegExp(`^a=fmtp:${pt}\\s`).test(line)) {
      return `a=fmtp:${pt} ${params}`;
    }
    // Hoist Opus to the front of the codec list on the audio m-line.
    if (/^m=audio\s/.test(line)) {
      const parts = line.split(' ');
      const head = parts.slice(0, 3); // "m=audio", port, proto
      const pts = parts.slice(3).filter((p) => p !== pt);
      return [...head, pt, ...pts].join(' ');
    }
    return line;
  });

  // If Opus had no fmtp line at all, inject one right after its rtpmap.
  if (!out.some((l) => new RegExp(`^a=fmtp:${pt}\\s`).test(l))) {
    const idx = out.findIndex((l) => new RegExp(`^a=rtpmap:${pt}\\s`).test(l));
    if (idx !== -1) out.splice(idx + 1, 0, `a=fmtp:${pt} ${params}`);
  }
  return out.join('\r\n');
}

/**
 * Wraps a single RTCPeerConnection for a 1-to-1 audio call. Owns the local mic
 * stream and surfaces ICE candidates, the remote stream, and connection-state
 * changes via callbacks. No signaling here — the caller (CallContext) ships the
 * SDP/ICE over the WebSocket.
 */
export class PeerService {
  private pc: RTCPeerConnection;
  private localStream: MediaStream | null = null;
  private ownsStream = false;

  onIceCandidate: CandidateCb | null = null;
  onRemoteStream: StreamCb | null = null;
  onConnectionStateChange: StateCb | null = null;

  constructor(iceServers: IceServer[]) {
    this.pc = new RTCPeerConnection({ iceServers });

    this.pc.onicecandidate = (e) => {
      if (e.candidate && this.onIceCandidate) {
        this.onIceCandidate(e.candidate.toJSON());
      }
    };
    this.pc.ontrack = (e) => {
      if (e.streams[0] && this.onRemoteStream) {
        this.onRemoteStream(e.streams[0]);
      }
    };
    this.pc.onconnectionstatechange = () => {
      this.onConnectionStateChange?.(this.pc.connectionState);
    };
  }

  /** getUserMedia(audio) with quality constraints + attach the mic track. */
  async initLocalAudio(): Promise<void> {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
    } catch {
      // Some browsers reject exact constraints — retry with the bare request.
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }
    this.ownsStream = true;
    this.localStream.getTracks().forEach((t) => this.pc.addTrack(t, this.localStream!));
    this.applySenderBitrate();
  }

  /**
   * Mesh: attach an externally-owned shared mic stream. NOT owned — close() will
   * not stop its tracks (the MeshCallSession owns the single shared stream and
   * stops it once, so closing one peer never kills audio for the others).
   */
  addLocalStream(stream: MediaStream): void {
    this.localStream = stream;
    this.ownsStream = false;
    stream.getTracks().forEach((t) => this.pc.addTrack(t, stream));
    this.applySenderBitrate();
  }

  /** Floor the encoder bitrate and flag the call as high network priority. */
  private applySenderBitrate(): void {
    try {
      const sender = this.pc.getSenders().find((s) => s.track?.kind === 'audio');
      if (!sender) return;
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      params.encodings[0].maxBitrate = OPUS_MAX_BITRATE;
      (params.encodings[0] as any).networkPriority = 'high';
      void sender.setParameters(params).catch(() => {});
    } catch {
      /* setParameters unsupported on some browsers — non-fatal */
    }
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc.createOffer();
    offer.sdp = offer.sdp ? tuneOpusSdp(offer.sdp) : offer.sdp;
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  async createAnswer(): Promise<RTCSessionDescriptionInit> {
    const answer = await this.pc.createAnswer();
    answer.sdp = answer.sdp ? tuneOpusSdp(answer.sdp) : answer.sdp;
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  async setRemoteDescription(desc: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(new RTCSessionDescription(desc));
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {
      /* a late/duplicate candidate after close is non-fatal */
    }
  }

  /** Mute/unmute the local mic by toggling the audio track. */
  setMuted(muted: boolean): void {
    this.localStream?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
  }

  close(): void {
    if (this.ownsStream) this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    try { this.pc.close(); } catch { /* already closed */ }
  }
}
